import type { WireInlineScannerComponent } from "@glide/fluent-components/dist/js/base-components";
import type { InlineScanner } from "@glide/fluent-components/dist/js/fluent-components";
import { getActionProperty } from "@glide/app-description";
import { getFeatureSetting } from "@glide/common-core/dist/js/feature-settings";
import { isRequiredPropertyHandler } from "@glide/function-utils";
import { isBound, type WireAlwaysEditableValue, WireComponentKind } from "@glide/wire";
import { definedMap } from "collection-utils";

import {
    hydrateSubAction,
    inflateActions,
    inflateComponentEnricher,
    inflateStringProperty,
    makeSimpleWireRowComponentHydratorConstructor,
} from "../wire/utils";
import type { ComponentInflator } from "./fluent-components-handlers";

export const inlineScannerInflator: ComponentInflator<typeof InlineScanner> = (ib, desc) => {
    const scanAction = getActionProperty(desc.action);
    const onScanHydrator = definedMap(scanAction, a => inflateActions(ib, [a]));

    const { tokenMaker, tableAndColumn } = ib.getValueSetterForProperty(desc.writeTo, "set-scanned-column");
    if (tableAndColumn === undefined) return undefined;

    const isRequired = getFeatureSetting("requiredForMoreComponents") && isRequiredPropertyHandler.getDefinedSwitch(desc);
    const [writeToValueGetter] = inflateStringProperty(ib, desc.writeTo, false);

    const componentEnricher = inflateComponentEnricher<WireInlineScannerComponent>(ib, desc);

    return makeSimpleWireRowComponentHydratorConstructor(hb => {
        const onChangeToken = tokenMaker(hb, async ab => {
            if (onScanHydrator === undefined) {
                return;
            }

            const runner = await hydrateSubAction(ab, onScanHydrator);
            await ab.invoke("code scanned", runner, false);

            return;
        });

        if (onChangeToken === false || onChangeToken === undefined) {
            return undefined;
        }

        const editableScan: WireAlwaysEditableValue<string> = {
            // We don't care what the value is, we just want to set a new one.
            value: "dummy-we-dont-need-this",
            onChangeToken,
        };

        const writeToValue = writeToValueGetter(hb);
        const hasValue = isBound(writeToValue) && writeToValue !== "";

        return {
            component: componentEnricher({
                kind: WireComponentKind.InlineScanner,
                editableScan,
                isRequired,
            }),
            isValid: !isRequired || hasValue,
        };
    });
};
